'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  ShieldCheck,
  BookOpen,
  Headphones,
  ArrowRight,
  Phone as PhoneIcon,
  Plus
} from 'lucide-react';
import { PhoneNumberHeader } from './PhoneNumberHeader';
import { Skeleton } from '@/shared/components/skeletons';
import { useAppSelector } from '@/core/store/hooks';
import { useGetOrganizationPhoneNumbersQuery, useGetOrganizationConsentSettingsQuery } from '@/api/features/organizations';
import { useSecureAuthContext } from '@/core/auth/services';

import {
  PhoneNumberCard,
  AvailablePhoneNumbers,
  ConsentManagement,
} from './components';
import { useSocket } from '@/core/providers/SocketProvider';
import { ServerEvents } from '@voley/shared-types/socket';
import type { CampaignViewModel, PhoneNumberApiResponseViewModel } from '../../types';

// Compliance Checklist (inline)
interface ComplianceItem {
  label: string;
  status: 'pass' | 'warn' | 'fail';
  detail: string;
}

function ComplianceChecklist({
  campaign,
  consentMessage,
  consentData,
}: {
  campaign?: CampaignViewModel | null;
  consentMessage: string;
  consentData?: PhoneNumberApiResponseViewModel['consent'];
}) {
  const items: ComplianceItem[] = [];

  // 10DLC
  const isApproved = campaign?.status === 'approved' || campaign?.status === 'connected';
  const hasCampaign = !!campaign;
  items.push({
    label: '10DLC Registration',
    status: isApproved ? 'pass' : hasCampaign ? 'warn' : 'fail',
    detail: isApproved
      ? 'Campaign approved'
      : hasCampaign
      ? 'Registration pending review'
      : 'Not registered — required for A2P messaging',
  });

  // Consent message configured
  const hasMessage = !!consentMessage && consentMessage.trim().length > 0;
  items.push({
    label: 'Consent Message',
    status: hasMessage ? 'pass' : 'fail',
    detail: hasMessage ? 'Consent message configured' : 'No consent message — contacts won\'t receive opt-in language',
  });

  // STOP/HELP compliance
  const upper = consentMessage?.toUpperCase() ?? '';
  const hasStop = upper.includes('STOP');
  const hasHelp = upper.includes('HELP');
  items.push({
    label: 'STOP/HELP Language',
    status: hasStop && hasHelp ? 'pass' : 'warn',
    detail: hasStop && hasHelp
      ? 'Message includes required STOP and HELP instructions'
      : `Missing ${!hasStop ? 'STOP' : ''}${!hasStop && !hasHelp ? ' and ' : ''}${!hasHelp ? 'HELP' : ''} instructions in consent message`,
  });

  // Consent tracking
  const totalContacts = consentData?.totalContacts ?? 0;
  items.push({
    label: 'Consent Tracking',
    status: totalContacts > 0 ? 'pass' : 'warn',
    detail: totalContacts > 0
      ? `Tracking consent for ${totalContacts.toLocaleString()} contacts`
      : 'No contacts tracked yet — consent data will populate as you message',
  });

  const passCount = items.filter(i => i.status === 'pass').length;

  const StatusIcon = ({ status }: { status: ComplianceItem['status'] }) => {
    switch (status) {
      case 'pass': return <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />;
      case 'warn': return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
      case 'fail': return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
    }
  };

  return (
    <div className="bg-card-background rounded-lg border border-border p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-text-secondary" />
          <h2 className="text-base font-semibold text-text-primary">Compliance Readiness</h2>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
          passCount === items.length
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
        }`}>
          {passCount}/{items.length} passed
        </span>
      </div>
      <div className="space-y-2.5">
        {items.map((item) => (
          <div key={item.label} className="flex items-start gap-3 py-1.5">
            <StatusIcon status={item.status} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-text-primary">{item.label}</p>
              <p className="text-xs text-text-secondary">{item.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Docs & Support Links
function DocsAndSupport() {
  return (
    <div className="bg-card-background rounded-lg border border-border p-5">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="h-4 w-4 text-text-secondary" />
        <h3 className="text-sm font-medium text-text-primary">Resources &amp; Support</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Link
          href="/docs/porting"
          className="flex items-center gap-2 p-2.5 rounded-lg border border-border hover:bg-hover-background transition-colors group"
        >
          <ArrowRight className="h-3.5 w-3.5 text-text-secondary group-hover:text-primary transition-colors" />
          <div>
            <p className="text-sm font-medium text-text-primary">Port a number</p>
            <p className="text-xs text-text-secondary">Transfer an existing number</p>
          </div>
        </Link>
        <Link
          href="/docs/releasing"
          className="flex items-center gap-2 p-2.5 rounded-lg border border-border hover:bg-hover-background transition-colors group"
        >
          <ArrowRight className="h-3.5 w-3.5 text-text-secondary group-hover:text-primary transition-colors" />
          <div>
            <p className="text-sm font-medium text-text-primary">Release a number</p>
            <p className="text-xs text-text-secondary">Contact us to release</p>
          </div>
        </Link>
        <a
          href="mailto:support@voley.us"
          className="flex items-center gap-2 p-2.5 rounded-lg border border-border hover:bg-hover-background transition-colors group"
        >
          <Headphones className="h-3.5 w-3.5 text-text-secondary group-hover:text-primary transition-colors" />
          <div>
            <p className="text-sm font-medium text-text-primary">Talk to support</p>
            <p className="text-xs text-text-secondary">support@voley.us</p>
          </div>
        </a>
      </div>
    </div>
  );
}

// Main Component
const PhoneNumber: React.FC = () => {
  const { user: authUser } = useSecureAuthContext();
  const userProfile = useAppSelector(state => state.userProfile.session);
  const organizationId = userProfile?.organizationId || authUser?.organizationId;

  const {
    data: phoneNumbersData,
    isLoading: isLoadingPhoneNumbers,
    error: phoneNumbersError,
    refetch: refetchPhoneNumbers
  } = useGetOrganizationPhoneNumbersQuery(organizationId ?? '', {
    skip: !organizationId
  });

  const { on, off } = useSocket();

  const DEFAULT_CONSENT_MESSAGE = 'You are now subscribed to receive SMS messages from {org_name} regarding insurance services. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out. Reply HELP for help.';

  // Consent settings via RTK Query
  const { data: consentSettingsData } = useGetOrganizationConsentSettingsQuery(
    organizationId ?? '',
    { skip: !organizationId }
  );
  const consentMessage = consentSettingsData?.consent_message || DEFAULT_CONSENT_MESSAGE;
  const autoAppendFirst = consentSettingsData?.auto_append_first_message !== false;

  const apiData = phoneNumbersData as PhoneNumberApiResponseViewModel | undefined;
  const primaryPhoneNumber = apiData?.phoneNumbers?.[0];
  const consentData = apiData?.consent;
  const campaignData = apiData?.campaign;

  // Live refetch on usage updates via WebSocket
  useEffect(() => {
    if (!organizationId) return;
    const handler = (data: { organizationId?: string }) => {
      if (data?.organizationId === organizationId) {
        refetchPhoneNumbers().catch(() => {});
      }
    };
    on(ServerEvents.ORGANIZATION_USAGE_UPDATED, handler);
    return () => {
      off(ServerEvents.ORGANIZATION_USAGE_UPDATED, handler);
    };
  }, [organizationId, on, off, refetchPhoneNumbers]);

  // Loading state
  if (isLoadingPhoneNumbers) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <PhoneNumberHeader />
        <div className="bg-card-background rounded-lg border border-border p-6 space-y-4">
          <Skeleton className="h-6 w-48 mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (phoneNumbersError) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <PhoneNumberHeader />
        <div className="bg-card-background rounded-lg border border-border p-6">
          <p className="text-error">Failed to load phone number data.</p>
          <p className="text-sm mt-2 text-text-secondary">
            Error: {JSON.stringify(phoneNumbersError)}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 px-3 py-1 bg-primary text-white text-sm rounded hover:opacity-90 transition-opacity"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // Empty state — no phone number assigned
  if (!primaryPhoneNumber) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <PhoneNumberHeader />
        <div className="bg-card-background rounded-lg border border-border p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <PhoneIcon className="h-7 w-7 text-primary" />
          </div>
          <h2 className="text-lg font-semibold text-text-primary mb-2">No Phone Number Assigned</h2>
          <p className="text-sm text-text-secondary max-w-md mx-auto mb-5">
            Register a phone number to start sending messages. You&apos;ll need to complete 10DLC registration for carrier compliance.
          </p>
          <Link
            href="/10dlc"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Register a Phone Number
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header with Register button */}
      <PhoneNumberHeader />

      {/* PRIMARY: Number Details Panel */}
      <PhoneNumberCard
        phoneNumber={primaryPhoneNumber}
        campaign={campaignData}
      />

      {/* Compliance Readiness Checklist */}
      <ComplianceChecklist
        campaign={campaignData}
        consentMessage={consentMessage}
        consentData={consentData}
      />

      {/* SECONDARY: Consent Management */}
      <div className="bg-card-background rounded-lg border border-border p-6">
        <ConsentManagement
          consentData={{
            requestMessage: consentMessage,
            autoAddToFirstMessage: autoAppendFirst,
          }}
          compact
        />
      </div>

      {/* Docs & Support Links */}
      <DocsAndSupport />

      {/* TERTIARY: Manage Numbers (collapsed) */}
      <AvailablePhoneNumbers />
    </div>
  );
};

export default PhoneNumber;
